import { useState } from "react";
import moment from "moment-timezone";
import { useToast } from "@/components/ui/use-toast";
import {
  OtcReportDateRangeValidation,
  useGenerateFeeReport,
} from "@/lib/api/otc";
import { CommonDestinationTimezone } from "@/lib/utils";
import { BearDatePicker } from "../ContractForm";
import { ReportCard, ReportDownloadLink } from "./ReportCard";

interface FeeReportFilters {
  after_date?: Date;
  before_date?: Date;
}

/**
 * Fee Report — required after/before date range.
 */
export function FeeReportCard() {
  const { toast } = useToast();
  const generateReport = useGenerateFeeReport();
  const [filters, setFilters] = useState<FeeReportFilters>({});
  const [link, setLink] = useState<string | undefined>(undefined);
  const [isDownloading, setIsDownloading] = useState(false);

  const isDisabled = !filters.after_date || !filters.before_date;

  const onGenerate = async () => {
    if (isDownloading || isDisabled) return;
    setLink(undefined);
    setIsDownloading(true);

    try {
      const parsed = OtcReportDateRangeValidation.safeParse({
        after_date: filters.after_date
          ? moment.tz(filters.after_date, CommonDestinationTimezone).format("YYYY/MM/DD")
          : "",
        before_date: filters.before_date
          ? moment.tz(filters.before_date, CommonDestinationTimezone).format("YYYY/MM/DD")
          : "",
      });

      if (!parsed.success) {
        toast({ title: "Invalid Filters", description: "" });
        return;
      }

      const result = await generateReport(parsed.data);
      if (!result.success) {
        throw result;
      }
      setLink(result.data.link);
    } catch (error: unknown) {
      console.error("Failed to generate otc fee report", error);
      toast({ title: "Failed to generate", description: "" });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <ReportCard
      title="Fee Report"
      description=""
      isDownloading={isDownloading}
      isGenerateDisabled={isDisabled}
      onGenerate={onGenerate}
      hasResults={!!link}
      linkContent={link ? <ReportDownloadLink href={link} label="Download Report" /> : null}
    >
      <div className="grid grid-cols-1 gap-3">
        <BearDatePicker
          tabIndex={0}
          name="fee_report_after_date"
          label="After Date"
          value={filters.after_date}
          onValueChange={(date: Date | undefined) =>
            setFilters((prev: FeeReportFilters) => ({ ...prev, after_date: date }))
          }
        />
        <BearDatePicker
          tabIndex={1}
          name="fee_report_before_date"
          label="Before Date"
          value={filters.before_date}
          onValueChange={(date: Date | undefined) =>
            setFilters((prev: FeeReportFilters) => ({ ...prev, before_date: date }))
          }
        />
      </div>
    </ReportCard>
  );
}
